import { useCallback, useEffect, useLayoutEffect, useRef, type ClipboardEvent, type FormEvent } from 'react'
import { caretOffset, chipBeside, draw, hasBareNotionLink, isDrawn, placeCaret, read } from '@/lib/chipText'
import { isPhone } from '@/lib/links'
import type { NotionPage } from '@/lib/notion'
import './LinkedText.css'
import './DescriptionEditor.css'

interface DescriptionEditorProps {
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  label: string
  placeholder: string
}

// what turns a written-out Notion link into its chip: a paste or drop, a new line, a space or any
// insert of more than a letter (a keyboard's clipboard suggestion arrives as typed text), and leaving
// the box. Typing a link letter by letter keeps it as text until it is finished.
const CHIP_NOW = new Set(['insertFromPaste', 'insertFromDrop', 'insertParagraph', 'insertLineBreak', 'insertReplacementText'])
const chipNow = (inputType: string, data: string | null) => CHIP_NOW.has(inputType) || (!!data && (data.length > 1 || /\s/.test(data)))

/**
 * The detail panel's description: plain text you type in, as a textarea would take it, except that
 * a Notion page shows as its chip, here as everywhere else. What is saved is still the plain text,
 * with the link's address where the chip stands. Backspace or Delete beside a chip takes the whole
 * link; tapping a chip opens the page.
 *
 * Other web addresses and phone numbers show as links while you read: tapping one opens it or rings
 * it, as on a card. Once you are typing in the box they are text like the rest, so a tap there puts the
 * caret in them to change them.
 */
export function DescriptionEditor({ value, onChange, onBlur, label, placeholder }: DescriptionEditorProps) {
  const box = useRef<HTMLDivElement>(null)
  // the text the box holds now, so a value that only echoes what was typed here does not redraw it
  const shown = useRef<string | null>(null)

  const redraw = useCallback((text: string, caret?: number) => {
    const el = box.current
    if (!el) return
    const at = caret ?? (el.ownerDocument.activeElement === el ? caretOffset(el) : null)
    draw(el, text, notionChip)
    shown.current = text
    if (at !== null) placeCaret(el, Math.min(at, text.length))
  }, [])

  // on opening, and when the other person's edit arrives
  useLayoutEffect(() => {
    if (value !== shown.current) redraw(value)
  }, [value, redraw])

  const changed = useCallback(
    (chip: boolean) => {
      const el = box.current
      if (!el) return
      const text = read(el)
      const before = shown.current
      shown.current = text
      if (chip && hasBareNotionLink(el)) redraw(text)
      if (text !== before) onChange(text)
    },
    [onChange, redraw],
  )

  // a chip goes in one keystroke, on every keyboard, rather than however the browser deletes around it
  useEffect(() => {
    const el = box.current
    if (!el) return
    const onBeforeInput = (e: InputEvent) => {
      const backward = e.inputType === 'deleteContentBackward'
      if (!backward && e.inputType !== 'deleteContentForward') return
      const chip = chipBeside(el, backward)
      if (!chip) return
      e.preventDefault()
      const at = caretOffset(el) ?? 0
      const url = chip.dataset.url ?? ''
      chip.remove()
      redraw(read(el), backward ? at - url.length : at)
      changed(false)
    }
    el.addEventListener('beforeinput', onBeforeInput)
    return () => el.removeEventListener('beforeinput', onBeforeInput)
  }, [redraw, changed])

  const onInput = (e: FormEvent<HTMLDivElement>) => {
    const native = e.nativeEvent as InputEvent
    changed(!native.isComposing && chipNow(native.inputType, native.data))
  }

  // copying a chip copies its link, not the page name it shows
  const onCopy = (e: ClipboardEvent<HTMLDivElement>, cut: boolean) => {
    const sel = e.currentTarget.ownerDocument.getSelection()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return
    const range = sel.getRangeAt(0)
    const holder = e.currentTarget.ownerDocument.createElement('div')
    holder.append(range.cloneContents())
    e.clipboardData.setData('text/plain', read(holder))
    e.preventDefault()
    if (cut) {
      range.deleteContents()
      changed(false)
    }
  }

  const editing = () => !!box.current && box.current.ownerDocument.activeElement === box.current

  return (
    <div
      ref={box}
      className={`desc-editor ${value === '' ? 'desc-editor--empty' : ''}`}
      contentEditable="plaintext-only"
      role="textbox"
      aria-multiline="true"
      aria-label={label}
      aria-placeholder={placeholder}
      data-placeholder={placeholder}
      tabIndex={0}
      suppressContentEditableWarning
      onInput={onInput}
      onCompositionEnd={(e) => changed(chipNow('insertCompositionText', e.data))}
      onCopy={(e) => onCopy(e, false)}
      onCut={(e) => onCopy(e, true)}
      onMouseDown={(e) => {
        // a tap on a link while reading opens it, without the caret and the keyboard coming up
        if (linkAt(e.target) && !editing()) e.preventDefault()
      }}
      onClick={(e) => {
        const chip = (e.target as HTMLElement).closest<HTMLElement>('[data-url]')
        if (chip?.dataset.url) window.open(chip.dataset.url, '_blank', 'noopener,noreferrer')
        const href = linkAt(e.target)
        if (href && !editing()) {
          if (isPhone(href)) window.open(href, '_self')
          else window.open(href, '_blank', 'noopener,noreferrer')
        }
      }}
      onBlur={() => {
        // what was typed is marked up afresh: a link finished, or one that is no longer a link
        if (box.current && !isDrawn(box.current, notionChip)) redraw(read(box.current))
        onBlur()
      }}
    />
  )
}

/** Where the link under a tap goes, if the tap is on a web address or phone number. */
function linkAt(target: EventTarget): string | undefined {
  return (target as HTMLElement).closest<HTMLElement>('[data-href]')?.dataset.href
}

const SVG = 'http://www.w3.org/2000/svg'

/** The same chip LinkedText draws, made by hand because React does not own what is in the box. */
function notionChip(page: NotionPage): HTMLElement {
  const chip = document.createElement('span')
  chip.className = 'notion-chip notion-chip--link'
  chip.title = page.url
  const icon = document.createElementNS(SVG, 'svg')
  icon.setAttribute('class', 'notion-chip__icon')
  icon.setAttribute('viewBox', '0 0 24 24')
  icon.setAttribute('width', '14')
  icon.setAttribute('height', '14')
  icon.setAttribute('aria-hidden', 'true')
  icon.setAttribute('style', 'fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round')
  // NotionIcon's shapes
  const rect = document.createElementNS(SVG, 'rect')
  for (const [k, v] of Object.entries({ x: '4', y: '3', width: '16', height: '18', rx: '3' })) rect.setAttribute(k, v)
  const path = document.createElementNS(SVG, 'path')
  path.setAttribute('d', 'M9 16.5v-9l6 9v-9')
  icon.append(rect, path)
  chip.append(icon, page.title ?? 'Notion page')
  return chip
}
